import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tiers = [
  { value: 'basic', label: 'Basic Practice' },
  { value: 'comps_ready', label: 'Comps Ready' },
  { value: 'advanced', label: 'Advanced' },
];

const categories = {
  basic: [
    { value: 'acupuncture', label: 'Acupuncture' },
    { value: 'biomedicine', label: 'Biomedicine' },
    { value: 'foundations', label: 'Foundations' },
    { value: 'herbs', label: 'Herbs' },
    { value: 'law_safety', label: 'Law & Safety' },
  ],
  comps_ready: [
    { value: 'acupuncture', label: 'Acupuncture' },
    { value: 'biomedicine', label: 'Biomedicine' },
    { value: 'foundations', label: 'Foundations' },
    { value: 'herbs', label: 'Herbs' },
    { value: 'law_safety', label: 'Law & Safety' },
  ],
  advanced: [
    { value: 'luo_vessels', label: 'Luo Vessels' },
    { value: 'divergent_vessels', label: 'Divergent Vessels' },
    { value: 'adjunct_modalities', label: 'Adjunct Modalities' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'alternate_needling', label: 'Alternate Needling Styles' },
    { value: 'advanced_needle', label: 'Advanced Needle Technique' },
    { value: 'sports_medicine', label: 'Sports Medicine Acupuncture' },
  ],
};

export default function TestManagementPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/admin/tests/manage`, {
        withCredentials: true
      });
      setTests(response.data.tests);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = (testId, newTier) => {
    setTests(prevTests =>
      prevTests.map(test =>
        test.id === testId ? { ...test, tier: newTier, category: '' } : test
      )
    );
  };

  const handleCategoryChange = (testId, newCategory) => {
    setTests(prevTests =>
      prevTests.map(test =>
        test.id === testId ? { ...test, category: newCategory } : test
      )
    );
  };

  const handleSave = async (testId) => {
    const test = tests.find(t => t.id === testId);
    
    if (!test.tier || !test.category) {
      toast.error('Please select both tier and category');
      return;
    }

    setSaving(testId);
    try {
      await axios.put(
        `${API}/admin/tests/${testId}/categorize`,
        {
          tier: test.tier,
          category: test.category
        },
        { withCredentials: true }
      );
      
      toast.success('Test categorized successfully!');
    } catch (error) {
      console.error('Error saving test:', error);
      toast.error('Failed to save test categorization');
    } finally {
      setSaving(null);
    }
  };

  const getTierLabel = (tier) => {
    const found = tiers.find(t => t.value === tier);
    return found ? found.label : tier;
  };

  const getCategoryLabel = (tier, category) => {
    const tierCategories = categories[tier] || [];
    const found = tierCategories.find(c => c.value === category);
    return found ? found.label : category;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-5xl retro-title">Test Management</h1>
            <button
              onClick={fetchTests}
              className="retro-button flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-xl text-white">Loading tests...</p>
            </div>
          ) : tests.length === 0 ? (
            <div className="cassette-card p-12 text-center">
              <p className="text-2xl text-[#6B4423]">No tests found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tests.map((test) => (
                <div key={test.id} className="cassette-card p-6">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    {/* Test Info */}
                    <div className="md:col-span-4">
                      <h3 className="text-xl font-bold text-[#2C1810] mb-1">{test.title}</h3>
                      <p className="text-sm text-[#6B4423] mb-2">{test.description}</p>
                      <div className="text-xs text-[#6B4423]">
                        {test.question_count} questions • {test.passing_score}% passing
                      </div>
                    </div>

                    {/* Tier Select */}
                    <div className="md:col-span-3">
                      <label className="block text-white font-bold text-sm mb-1">Tier</label>
                      <select
                        value={test.tier || ''}
                        onChange={(e) => handleTierChange(test.id, e.target.value)}
                        className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded text-gray-900"
                      >
                        <option value="">Select Tier</option>
                        {tiers.map(tier => (
                          <option key={tier.value} value={tier.value}>
                            {tier.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Category Select */}
                    <div className="md:col-span-3">
                      <label className="block text-white font-bold text-sm mb-1">Section</label>
                      <select
                        value={test.category || ''}
                        onChange={(e) => handleCategoryChange(test.id, e.target.value)}
                        disabled={!test.tier}
                        className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded text-gray-900 disabled:opacity-50"
                      >
                        <option value="">Select Section</option>
                        {test.tier && categories[test.tier]?.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Save Button */}
                    <div className="md:col-span-2 flex items-end">
                      <button
                        onClick={() => handleSave(test.id)}
                        disabled={saving === test.id || !test.tier || !test.category}
                        className="retro-button w-full flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        {saving === test.id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>

                  {/* Current Assignment */}
                  {test.tier && test.category && (
                    <div className="mt-3 pt-3 border-t border-[#8B6F47]">
                      <span className="text-xs text-[#6B4423]">
                        Currently assigned to: <strong>{getTierLabel(test.tier)}</strong> → <strong>{getCategoryLabel(test.tier, test.category)}</strong>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
